import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Alert,
  Container,
  Snackbar,
  TablePagination,
  Checkbox,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Launch,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { competitorBacklinksService } from '../services/competitorBacklinksService';
import { useAuth } from '../contexts/AuthContext';

/** Match Guest Blog Sites page shell: filter bar + table container */
const drRequestsPageStyles = `
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }
  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #1E293B;
    margin: 0;
  }
  .page-subtitle {
    font-size: 14px;
    color: #64748B;
    margin: -8px 0 20px 0;
    max-width: 840px;
    line-height: 1.5;
  }
  .sites-filter-bar {
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(0,0,0,.05);
    padding: 12px 14px;
    margin-bottom: 10px;
  }
  .sites-filter-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .filter-search {
    display: flex;
    align-items: center;
    gap: 7px;
    background: #F1F5F9;
    border: 1px solid #E2E8F0;
    border-radius: 6px;
    padding: 6px 10px;
    min-width: 240px;
    flex: 1;
    max-width: 420px;
    transition: all 0.15s;
  }
  .filter-search:focus-within {
    background: #FFFFFF;
    border-color: #2563EB;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
  }
  .filter-search svg {
    width: 13px;
    height: 13px;
    color: #94A3B8;
    flex-shrink: 0;
  }
  .filter-search input {
    border: none;
    background: none;
    outline: none;
    font-family: inherit;
    font-size: 13px;
    color: #0F172A;
    width: 100%;
  }
  .filter-search input::placeholder {
    color: #94A3B8;
  }
  .filter-divider {
    width: 1px;
    height: 18px;
    background: #E2E8F0;
    flex-shrink: 0;
  }
  .filter-select {
    appearance: none;
    border: 1px solid #E2E8F0;
    border-radius: 6px;
    padding: 5px 26px 5px 9px;
    font-family: inherit;
    font-size: 12.5px;
    color: #475569;
    background: #F1F5F9;
    outline: none;
    cursor: pointer;
    transition: all 0.15s;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 7px center;
    min-width: 130px;
  }
  .filter-select:focus {
    border-color: #2563EB;
    background-color: #FFFFFF;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
  }
  .filter-select:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .filter-spacer {
    flex: 1;
    min-width: 8px;
  }
  .action-btn {
    padding: 6px 10px;
    border-radius: 6px;
    border: 1px solid #E2E8F0;
    background: #FFFFFF;
    cursor: pointer;
    transition: all 0.15s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;
    color: #475569;
    font-family: inherit;
  }
  .action-btn:hover {
    background: #FFFFFF;
    border-color: #94A3B8;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
  .action-btn svg {
    width: 16px;
    height: 16px;
    color: #64748B;
  }
  .action-btn-danger {
    border-color: #FECACA;
    color: #B91C1C;
    background: #FEF2F2;
  }
  .action-btn-danger:hover:not(:disabled) {
    border-color: #F87171;
    background: #FEE2E2;
    box-shadow: 0 1px 2px rgba(185, 28, 28, 0.12);
  }
  .action-btn-danger:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  .dr-delete-col {
    width: 44px;
    text-align: center;
    vertical-align: middle;
  }
  /* Sites table — same rules as GuestBlogSitesPage */
  .sites-table-container {
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(0,0,0,.05);
    overflow-x: auto;
  }
  .sites-table-wrapper {
    min-width: 100%;
    overflow-x: auto;
  }
  .sites-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  .sites-table th {
    background: #F8FAFC;
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    font-size: 12px;
    color: #475569;
    border-bottom: 1px solid #E2E8F0;
    white-space: nowrap;
    letter-spacing: 0.3px;
  }
  .sites-table td {
    padding: 12px 16px;
    border-bottom: 1px solid #F1F5F9;
    vertical-align: middle;
  }
  .sites-table tr:hover td {
    background: #F8FAFC;
  }
  .sites-table tr:last-child td {
    border-bottom: none;
  }
  .sites-table .checkbox-cell {
    width: 48px;
    text-align: center;
    vertical-align: middle;
    padding-left: 12px;
    padding-right: 8px;
  }
  .site-url-cell {
    min-width: 200px;
    max-width: 300px;
  }
  .site-url-link {
    color: #2563EB;
    text-decoration: none;
    font-weight: 500;
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .site-url-link:hover {
    text-decoration: underline;
  }
  .dr-empty {
    text-align: center;
    padding: 48px 24px;
    color: #64748B;
  }
  .dr-empty h3 {
    margin: 0 0 8px 0;
    font-size: 16px;
    font-weight: 600;
    color: #475569;
  }
`;

const formatCompactNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined || Number.isNaN(num)) return 'N/A';
  if (num < 1000) return num.toString();
  const thousands = num / 1000;
  const millions = num / 1000000;
  const billions = num / 1000000000;
  if (num >= 1000000000) {
    return billions % 1 === 0 ? `${Math.floor(billions)}B` : `${billions.toFixed(1)}B`;
  }
  if (num >= 1000000) {
    return millions % 1 === 0 ? `${Math.floor(millions)}M` : `${millions.toFixed(1)}M`;
  }
  return thousands % 1 === 0 ? `${Math.floor(thousands)}k` : `${thousands.toFixed(1)}k`;
};

function formatLastSeen(dateString: string | null | undefined): string {
  if (!dateString) return 'N/A';
  const formatDate = (s: string) => {
    const date = new Date(s);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);
  if (diffHours < 1) {
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    return diffMinutes <= 1 ? '1m ago' : `${diffMinutes}m ago`;
  }
  if (diffHours < 24) {
    return diffHours === 1 ? '1h ago' : `${diffHours}h ago`;
  }
  if (diffDays < 30) {
    return diffDays === 1 ? '1d ago' : `${diffDays}d ago`;
  }
  return formatDate(dateString);
}

type AssignedAdmin = { id: string; name: string; email: string };

type DrRequestRow = {
  id: string;
  competitorWebsite: string;
  backlinkUrl: string;
  backlinkTitle?: string | null;
  backlinkDr?: number | null;
  isDofollow: boolean;
  isNofollow: boolean;
  isUgc: boolean;
  isSponsored: boolean;
  isContent: boolean;
  referringDomains?: string | null;
  lastSeen?: string | null;
  anchorText?: string | null;
  targetUrl?: string | null;
  /** Set after CSV export from this page — row appears under Exported tab */
  drRequestsExportedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  client?: { id: string; name: string; email: string };
  project?: { id: string; projectName: string };
  assignedAdmins?: AssignedAdmin[];
  /** Canonical group members represented by this row (for export/delete selection). */
  mergedSourceIds?: string[];
  /** Distinct clients that requested this canonical URL (for badges/filtering). */
  mergedClients?: { id: string; name: string; email?: string }[];
  /** Distinct project names represented in this grouped row. */
  mergedProjectNames?: string[];
};

type ExportTab = 'pending' | 'exported';

function normalizeCompetitorKeyForDedupe(raw: string): string {
  return (raw || '')
    .toLowerCase()
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '');
}

/** Canonical referring domain for grouping (hostname-level). */
function normalizeBacklinkUrlKeyForDedupe(raw: string): string {
  const t = (raw || '').trim();
  if (!t) return '';
  try {
    const withProto = /^https?:\/\//i.test(t) ? t : `https://${t}`;
    const u = new URL(withProto);
    return u.hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return t
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0]
      .split('?')[0]
      .split('#')[0]
      .trim();
  }
}

/** One row per analyzed site + referring domain (across clients); keeps highest DR representative. */
function dedupeDrCalculatorRows(list: DrRequestRow[]): DrRequestRow[] {
  const groups = new Map<string, DrRequestRow[]>();
  for (const r of list) {
    const k = [
      normalizeCompetitorKeyForDedupe(r.competitorWebsite),
      normalizeBacklinkUrlKeyForDedupe(r.backlinkUrl),
    ].join('|');
    const g = groups.get(k);
    if (g) g.push(r);
    else groups.set(k, [r]);
  }

  return Array.from(groups.values()).map((group) => {
    const sorted = [...group].sort((a, b) => (b.backlinkDr ?? -1) - (a.backlinkDr ?? -1));
    const best = sorted[0];
    const mergedSourceIds = group.map((x) => x.id);
    const clientsMap = new Map<string, { id: string; name: string; email?: string }>();
    for (const g of group) {
      const cid = g.client?.id;
      if (!cid) continue;
      clientsMap.set(cid, {
        id: cid,
        name: g.client?.name || g.client?.email || 'Client',
        email: g.client?.email,
      });
    }
    const mergedClients = Array.from(clientsMap.values());
    const mergedProjectNames = Array.from(
      new Set(
        group
          .map((x) => x.project?.projectName?.trim())
          .filter((x): x is string => !!x)
      )
    );
    return {
      ...best,
      mergedSourceIds,
      mergedClients,
      mergedProjectNames,
    };
  });
}

function rowMatchesSearch(row: DrRequestRow, q: string): boolean {
  if (!q.trim()) return true;
  const s = q.toLowerCase();
  const hay = [
    row.client?.name,
    row.client?.email,
    row.project?.projectName,
    row.competitorWebsite,
    row.backlinkUrl,
    row.backlinkTitle,
    row.anchorText,
    row.targetUrl,
    ...(row.assignedAdmins || []).map((a) => `${a.name} ${a.email}`),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(s);
}

function rowMatchesClient(row: DrRequestRow, clientId: string): boolean {
  if (!clientId) return true;
  return (row.mergedClients ?? []).some((c) => c.id === clientId);
}

function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value).replace(/\r?\n/g, ' ');
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Main domain for CSV "Site": hostname only from the referring page URL.
 * Examples: `https://torrisinyc.com` → `torrisinyc.com`, `https://www.torrisinyc.com/a/b` → `torrisinyc.com`
 * (scheme, path, query, hash, and default `www.` are dropped).
 */
function referringDomainFromBacklinkUrl(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  try {
    const withProto = /^https?:\/\//i.test(t) ? t : `https://${t}`;
    const u = new URL(withProto);
    let host = u.hostname.toLowerCase();
    if (host.endsWith('.')) host = host.slice(0, -1);
    return host.replace(/^www\./, '');
  } catch {
    let host = t
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .split('/')[0]
      .split('?')[0]
      .split('#')[0]
      .split('@')
      .pop()!
      .trim();
    if (host.includes(':') && !host.includes(']')) {
      const [h, maybePort] = host.split(':');
      if (/^\d+$/.test(maybePort || '')) host = h;
    }
    if (host.endsWith('.')) host = host.slice(0, -1);
    return host.replace(/^www\./, '');
  }
}

/**
 * CSV: Site = referring domain (from Referring page URL, not full URL, not analyzed site), DR.
 * Bulk: one row per unique domain; highest DR kept when the same domain appears on multiple rows.
 */
function buildDrRequestsExportCsv(data: DrRequestRow[]): {
  csv: string;
  uniqueDomainCount: number;
  selectedRowCount: number;
} {
  const selectedRowCount = data.length;
  const byDomain = new Map<string, { site: string; dr: number }>();

  for (const r of data) {
    const url = (r.backlinkUrl || '').trim();
    if (!url) continue;
    const domain = referringDomainFromBacklinkUrl(url);
    if (!domain) continue;
    const dr = r.backlinkDr ?? 0;
    const prev = byDomain.get(domain);
    if (!prev || dr > prev.dr) {
      byDomain.set(domain, { site: domain, dr });
    }
  }

  const sorted = Array.from(byDomain.values()).sort((a, b) =>
    a.site.localeCompare(b.site, undefined, { sensitivity: 'base' })
  );
  const lines = [['Site', 'DR'].join(','), ...sorted.map((row) => [csvEscape(row.site), csvEscape(row.dr)].join(','))];

  return {
    csv: lines.join('\r\n'),
    uniqueDomainCount: sorted.length,
    selectedRowCount,
  };
}

function downloadCsv(filename: string, csvBody: string) {
  const blob = new Blob(['\ufeff' + csvBody], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const DR_CALC_PENDING_COUNT_QUERY_KEY = ['dr-calculator-pending-count'] as const;

const DrCalculatorSiteRequestsPage: React.FC = () => {
  const { isSuperAdmin, hasPermission } = useAuth();
  const canAccess = isSuperAdmin || hasPermission('competitor_requests');
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<DrRequestRow[]>([]);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [clientFilterId, setClientFilterId] = useState('');
  const [exportTab, setExportTab] = useState<ExportTab>('pending');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [selectedIds, setSelectedIds] = useState(() => new Set<string>());
  const [markingExported, setMarkingExported] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!canAccess) return;
    try {
      setLoading(true);
      setError('');
      const data = await competitorBacklinksService.getDrCalculatorSiteRequests();
      setRows(data as DrRequestRow[]);
      void queryClient.invalidateQueries({ queryKey: DR_CALC_PENDING_COUNT_QUERY_KEY });
    } catch (e) {
      setError('Failed to load DR calculator site requests');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [canAccess, queryClient]);

  useEffect(() => {
    if (canAccess) void load();
  }, [canAccess, load]);

  const pendingCount = useMemo(
    () => dedupeDrCalculatorRows(rows.filter((r) => !r.drRequestsExportedAt)).length,
    [rows]
  );
  const exportedCount = useMemo(
    () => dedupeDrCalculatorRows(rows.filter((r) => !!r.drRequestsExportedAt)).length,
    [rows]
  );

  const rowsByTab = useMemo(() => {
    const raw = rows.filter((r) =>
      exportTab === 'pending' ? !r.drRequestsExportedAt : !!r.drRequestsExportedAt
    );
    const maxUpdatedByGlobalUrl = new Map<string, number>();
    for (const r of raw) {
      const k = normalizeBacklinkUrlKeyForDedupe(r.backlinkUrl);
      const t = Math.max(
        r.updatedAt ? new Date(r.updatedAt).getTime() : 0,
        r.createdAt ? new Date(r.createdAt).getTime() : 0
      );
      const prev = maxUpdatedByGlobalUrl.get(k) ?? 0;
      if (t > prev) maxUpdatedByGlobalUrl.set(k, t);
    }
    const deduped = dedupeDrCalculatorRows(raw);
    return deduped.sort((a, b) => {
      const ka = normalizeBacklinkUrlKeyForDedupe(a.backlinkUrl);
      const kb = normalizeBacklinkUrlKeyForDedupe(b.backlinkUrl);
      const ta = maxUpdatedByGlobalUrl.get(ka) ?? 0;
      const tb = maxUpdatedByGlobalUrl.get(kb) ?? 0;
      return tb - ta;
    });
  }, [rows, exportTab]);

  const clientsWithRequests = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const r of rowsByTab) {
      (r.mergedClients ?? []).forEach((c) => map.set(c.id, { id: c.id, name: c.name }));
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [rowsByTab]);

  useEffect(() => {
    if (clientFilterId && !clientsWithRequests.some((c) => c.id === clientFilterId)) {
      setClientFilterId('');
    }
  }, [clientsWithRequests, clientFilterId]);

  const filteredRows = useMemo(
    () =>
      rowsByTab.filter((r) => rowMatchesClient(r, clientFilterId) && rowMatchesSearch(r, searchInput)),
    [rowsByTab, clientFilterId, searchInput]
  );

  useEffect(() => {
    const valid = new Set(filteredRows.flatMap((r) => r.mergedSourceIds ?? [r.id]));
    setSelectedIds((prev) => {
      const next = new Set<string>();
      Array.from(prev).forEach((id) => {
        if (valid.has(id)) next.add(id);
      });
      const prevArr = Array.from(prev);
      if (next.size === prev.size && prevArr.every((id) => next.has(id))) return prev;
      return next;
    });
  }, [filteredRows]);

  const selectedExportRows = useMemo(
    () =>
      filteredRows.filter((r) =>
        (r.mergedSourceIds ?? [r.id]).every((id) => selectedIds.has(id))
      ),
    [filteredRows, selectedIds]
  );

  const headerAllFilteredSelected =
    filteredRows.length > 0 &&
    filteredRows.every((r) => (r.mergedSourceIds ?? [r.id]).every((id) => selectedIds.has(id)));
  const headerSomeFilteredSelected =
    filteredRows.some((r) => (r.mergedSourceIds ?? [r.id]).some((id) => selectedIds.has(id))) &&
    !headerAllFilteredSelected;

  const toggleSelectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allOn =
        filteredRows.length > 0 &&
        filteredRows.every((r) => (r.mergedSourceIds ?? [r.id]).every((id) => next.has(id)));
      if (allOn) {
        filteredRows.forEach((r) => (r.mergedSourceIds ?? [r.id]).forEach((id) => next.delete(id)));
      } else {
        filteredRows.forEach((r) => (r.mergedSourceIds ?? [r.id]).forEach((id) => next.add(id)));
      }
      return next;
    });
  };

  const toggleRowSelected = (row: DrRequestRow) => {
    const ids = row.mergedSourceIds ?? [row.id];
    const allSelected = ids.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  useEffect(() => {
    setPage(0);
  }, [searchInput, clientFilterId, exportTab]);

  useEffect(() => {
    const total = filteredRows.length;
    const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [filteredRows.length, rowsPerPage, page]);

  const paginatedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const afterCsvMarkExported = async (exportedRowIds: string[]) => {
    if (exportedRowIds.length === 0) return;
    try {
      setMarkingExported(true);
      await competitorBacklinksService.markDrCalculatorRequestsExported(exportedRowIds);
      await load();
    } catch (e) {
      console.error(e);
      setSnackbar({
        open: true,
        message: 'CSV downloaded, but saving export status failed. Refresh the page and try again.',
        severity: 'error',
      });
    } finally {
      setMarkingExported(false);
    }
  };

  const deleteRequestsByIds = async (ids: string[]) => {
    if (ids.length === 0) return;
    try {
      setDeleting(true);
      const { deleted } = await competitorBacklinksService.deleteDrCalculatorSiteRequests(ids);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      setSnackbar({
        open: true,
        message: deleted === 0 ? 'No matching rows were deleted.' : `Deleted ${deleted} request(s).`,
        severity: deleted === 0 ? 'error' : 'success',
      });
      await load();
    } catch (e) {
      console.error(e);
      setSnackbar({
        open: true,
        message: 'Delete failed. Check your connection and try again.',
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  const confirmDeleteSelected = () => {
    const ids = selectedExportRows.flatMap((r) => r.mergedSourceIds ?? [r.id]);
    if (ids.length === 0) {
      setSnackbar({
        open: true,
        message: 'Select one or more rows (use the header checkbox to select all in the list).',
        severity: 'error',
      });
      return;
    }
    if (
      !window.confirm(
        `Delete ${ids.length} selected DR calculator request(s)? This cannot be undone.`
      )
    ) {
      return;
    }
    void deleteRequestsByIds(ids);
  };

  const confirmDeleteOne = (row: DrRequestRow) => {
    const preview = `${row.competitorWebsite} — ${(row.backlinkTitle || row.backlinkUrl || '').slice(0, 80)}`;
    const n = row.mergedSourceIds?.length ?? 1;
    const msg =
      n > 1
        ? `Delete ${n} duplicate DB rows for this same referring domain?\n\n${preview}`
        : `Delete this DR calculator request?\n\n${preview}`;
    if (!window.confirm(msg)) return;
    void deleteRequestsByIds(row.mergedSourceIds ?? [row.id]);
  };

  if (!canAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <style>{drRequestsPageStyles}</style>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <div className="page-header">
          <h1 className="page-title">Competitor Backlink Site Requests          </h1>
        </div>
        <p className="page-subtitle">
          All client-requested referring sites from the DR Calculator (with client assignments). Pending = not yet
          exported; Exported = after CSV export.           CSV Site is the main domain extracted from the referring page URL
          (e.g. https://www.torrisinyc.com/path → torrisinyc.com), not the analyzed site.           The table merges duplicate rows
          that share the same referring domain (e.g. repeated Ahrefs saves). A “N clients” badge means different accounts
          requested the same domain. Rows sort by latest activity on that domain. Export/delete apply to every merged
          DB row for the selected line.
        </p>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={exportTab === 'pending' ? 0 : 1}
            onChange={(_, v) => setExportTab(v === 0 ? 'pending' : 'exported')}
            aria-label="Export status"
          >
            <Tab label={`Pending (${pendingCount})`} id="dr-tab-pending" aria-controls="dr-tab-panel" />
            <Tab label={`Exported (${exportedCount})`} id="dr-tab-exported" aria-controls="dr-tab-panel" />
          </Tabs>
        </Box>

        <div className="sites-filter-bar">
          <div className="sites-filter-row">
            <div className="filter-search">
              <SearchIcon />
              <input
                type="text"
                placeholder="Search by client, project, URL, anchor…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <div className="filter-divider" />
            <select
              className="filter-select"
              style={{ minWidth: '200px' }}
              value={clientFilterId}
              onChange={(e) => setClientFilterId(e.target.value)}
              disabled={rows.length === 0 || clientsWithRequests.length === 0}
              aria-label="Filter by client"
            >
              <option value="">All clients</option>
              {clientsWithRequests.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <div className="filter-divider" />
            <button
              type="button"
              className="action-btn action-btn-danger"
              disabled={loading || markingExported || deleting || selectedExportRows.length === 0}
              title="Permanently delete selected rows (requestStatus must be DR-requested)."
              onClick={confirmDeleteSelected}
            >
              <DeleteOutlineIcon style={{ fontSize: '16px', color: '#B91C1C' }} />
              Delete selected ({selectedExportRows.length})
            </button>
            <div className="filter-divider" />
            <button
              type="button"
              className="action-btn"
              disabled={loading || markingExported || deleting || selectedExportRows.length === 0}
              title="CSV Site = hostname from referring URL (https://… and paths removed). DR per unique domain (max if duplicate rows)."
              onClick={async () => {
                if (selectedExportRows.length === 0) {
                  setSnackbar({
                    open: true,
                    message: 'Select one or more rows (use the header checkbox to select all in the list).',
                    severity: 'error',
                  });
                  return;
                }
                const ids = selectedExportRows.flatMap((r) => r.mergedSourceIds ?? [r.id]);
                const { csv, uniqueDomainCount, selectedRowCount } = buildDrRequestsExportCsv(selectedExportRows);
                downloadCsv(
                  `dr-calculator-requests-${new Date().toISOString().slice(0, 10)}.csv`,
                  csv
                );
                const dedupNote =
                  selectedRowCount > uniqueDomainCount
                    ? ` (${uniqueDomainCount} unique domain(s) from ${selectedRowCount} row(s)).`
                    : ` (${uniqueDomainCount} domain(s)).`;
                setSnackbar({
                  open: true,
                  message: `Exported Site (domain) + DR.${dedupNote}`,
                  severity: 'success',
                });
                await afterCsvMarkExported(ids);
              }}
            >
              <DownloadIcon style={{ fontSize: '16px' }} />
              Export CSV ({selectedExportRows.length})
            </button>
            <div className="filter-spacer" />
            <button type="button" className="action-btn" onClick={() => load()} disabled={loading || markingExported || deleting}>
              <RefreshIcon style={{ fontSize: '16px' }} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', border: '1px solid #E2E8F0' }}>
            {error}
          </Alert>
        )}

        <div className="sites-table-container">
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={36} />
            </Box>
          ) : rows.length === 0 ? (
            <div className="dr-empty">
              <h3>No DR calculator site requests</h3>
              <Typography variant="body2" color="text.secondary">
                Requests appear here after a client requests a site from the DR Calculator.
              </Typography>
            </div>
          ) : rowsByTab.length === 0 ? (
            <div className="dr-empty">
              <h3>{exportTab === 'pending' ? 'No pending requests' : 'Nothing exported yet'}</h3>
              <Typography variant="body2" color="text.secondary">
                {exportTab === 'pending'
                  ? 'All rows are in the Exported tab, or there are no requests. Switch to Exported to see previously downloaded rows.'
                  : 'Use the Pending tab: select rows (header checkbox = all), click Export CSV — those rows will move here after download.'}
              </Typography>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="dr-empty">
              <h3>
                {clientFilterId && !searchInput.trim()
                  ? 'No requests for this client'
                  : 'No matches'}
              </h3>
              <Typography variant="body2" color="text.secondary">
                {clientFilterId && !searchInput.trim()
                  ? 'Choose another client or clear the client filter.'
                  : 'Try another search or clear filters.'}
              </Typography>
            </div>
          ) : (
            <div className="sites-table-wrapper">
              <table className="sites-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th className="checkbox-cell">
                      <Checkbox
                        size="small"
                        checked={headerAllFilteredSelected}
                        indeterminate={headerSomeFilteredSelected}
                        onChange={() => toggleSelectAllFiltered()}
                        inputProps={{ 'aria-label': 'Select all rows matching current filters' }}
                        sx={{ p: 0.25 }}
                      />
                    </th>
                    <th>Client</th>
                    <th>Project</th>
                    <th>Assigned admins</th>
                    <th>Analyzed site</th>
                    <th className="site-url-cell">Referring page</th>
                    <th>DR</th>
                    <th>Link type</th>
                    <th>Ref. domains</th>
                    <th>Last seen</th>
                    <th className="site-url-cell">Anchor / target</th>
                    <th className="dr-delete-col" aria-label="Delete" />
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((backlink) => {
                    const rowIds = backlink.mergedSourceIds ?? [backlink.id];
                    const rowKey = rowIds.slice().sort().join('|');
                    const mergeCount = rowIds.length;
                    const globalClientCount = backlink.mergedClients?.length ?? 1;
                    const rowChecked = rowIds.every((id) => selectedIds.has(id));
                    const rowIndeterminate =
                      rowIds.some((id) => selectedIds.has(id)) && !rowChecked;
                    return (
                    <tr key={rowKey}>
                      <td className="checkbox-cell">
                        <Checkbox
                          size="small"
                          checked={rowChecked}
                          indeterminate={rowIndeterminate}
                          onChange={() => toggleRowSelected(backlink)}
                          inputProps={{ 'aria-label': `Select request ${rowKey}` }}
                          sx={{ p: 0.25 }}
                        />
                      </td>
                      <td>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#1E293B' }}>
                          {globalClientCount > 1 ? `${globalClientCount} clients` : backlink.client?.name || '—'}
                        </Typography>
                        {globalClientCount > 1 ? (
                          <Typography
                            variant="caption"
                            sx={{ color: '#64748B', wordBreak: 'break-all', display: 'block' }}
                          >
                            {(backlink.mergedClients ?? [])
                              .slice(0, 3)
                              .map((c) => c.name)
                              .join(', ')}
                            {(backlink.mergedClients?.length ?? 0) > 3 ? '…' : ''}
                          </Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: '#64748B', wordBreak: 'break-all', display: 'block' }}>
                            {backlink.client?.email || ''}
                          </Typography>
                        )}
                      </td>
                      <td>
                        <Typography variant="body2" sx={{ color: '#334155' }}>
                          {(backlink.mergedProjectNames?.length ?? 0) > 1
                            ? `${backlink.mergedProjectNames?.length} projects`
                            : backlink.project?.projectName || backlink.mergedProjectNames?.[0] || '—'}
                        </Typography>
                      </td>
                      <td>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(backlink.assignedAdmins || []).length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              None assigned
                            </Typography>
                          ) : (
                            (backlink.assignedAdmins || []).map((a) => (
                              <Chip key={a.id} label={a.name} size="small" variant="outlined" sx={{ height: 22, fontSize: '11px' }} />
                            ))
                          )}
                        </Box>
                      </td>
                      <td>
                        <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: '12px', color: '#334155' }}>
                          {backlink.competitorWebsite}
                        </Typography>
                      </td>
                      <td className="site-url-cell">
                        {backlink.backlinkTitle && (
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#2563EB' }}>
                            {backlink.backlinkTitle}
                          </Typography>
                        )}
                        <a
                          className="site-url-link"
                          href={backlink.backlinkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {backlink.backlinkUrl}
                        </a>
                        <Launch sx={{ ml: 0.5, fontSize: 14, color: '#94A3B8', verticalAlign: 'middle' }} />
                        {mergeCount > 1 && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#64748B', mt: 0.5 }}>
                            {mergeCount} duplicate records (same domain); showing highest DR
                          </Typography>
                        )}
                        {globalClientCount > 1 && (
                          <Tooltip
                            title={`This referring domain appears in the queue for ${globalClientCount} different clients. Each request is kept separate; sorted by most recent activity on this domain.`}
                          >
                            <Chip
                              label={`${globalClientCount} clients`}
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ mt: 0.5, fontSize: '10px', height: 22 }}
                            />
                          </Tooltip>
                        )}
                      </td>
                      <td>
                        <Typography variant="body2" fontWeight={700} sx={{ color: '#1E293B' }}>
                          {backlink.backlinkDr ?? 'N/A'}
                        </Typography>
                      </td>
                      <td>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {backlink.isDofollow && (
                            <Chip label="DoFollow" size="small" sx={{ height: 20, fontSize: '10px', bgcolor: '#4caf50', color: '#fff' }} />
                          )}
                          {backlink.isNofollow && (
                            <Chip label="NoFollow" size="small" sx={{ height: 20, fontSize: '10px', bgcolor: '#f44336', color: '#fff' }} />
                          )}
                          {backlink.isUgc && (
                            <Chip label="UGC" size="small" sx={{ height: 20, fontSize: '10px', bgcolor: '#ff9800', color: '#fff' }} />
                          )}
                          {backlink.isSponsored && (
                            <Chip label="Sponsored" size="small" sx={{ height: 20, fontSize: '10px', bgcolor: '#9c27b0', color: '#fff' }} />
                          )}
                          {backlink.isContent && (
                            <Chip label="Content" size="small" sx={{ height: 20, fontSize: '10px', bgcolor: '#2196f3', color: '#fff' }} />
                          )}
                          {!backlink.isDofollow &&
                            !backlink.isNofollow &&
                            !backlink.isUgc &&
                            !backlink.isSponsored &&
                            !backlink.isContent && (
                              <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                                N/A
                              </Typography>
                            )}
                        </Box>
                      </td>
                      <td>
                        <Typography variant="body2" sx={{ color: '#334155' }}>
                          {formatCompactNumber(
                            backlink.referringDomains ? parseInt(backlink.referringDomains, 10) : null
                          )}
                        </Typography>
                      </td>
                      <td>
                        <Typography variant="body2" sx={{ fontSize: '12px', color: '#64748B' }}>
                          {formatLastSeen(backlink.lastSeen)}
                        </Typography>
                      </td>
                      <td className="site-url-cell">
                        {backlink.anchorText && (
                          <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 0.5, color: '#334155' }}>
                            "{backlink.anchorText}"
                          </Typography>
                        )}
                        {backlink.targetUrl && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <a
                              className="site-url-link"
                              href={backlink.targetUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {backlink.targetUrl}
                            </a>
                            <Launch sx={{ fontSize: 14, color: '#94A3B8', flexShrink: 0 }} />
                          </Box>
                        )}
                      </td>
                      <td className="dr-delete-col">
                        <Tooltip title="Delete this request">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Delete request"
                              disabled={deleting}
                              onClick={() => confirmDeleteOne(backlink)}
                              sx={{ color: '#B91C1C' }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && rows.length > 0 && filteredRows.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default DrCalculatorSiteRequestsPage;
